/**
 * The geometry search implementation. Creates a new instance of service call. Parameters can be passed on creation or
 * provided via convenient functions that can be chained. The method go() performs the actual call.
 * The call is asynchronous therefore user have two options to handle response,
 * either by passing callback function or by using
 * [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 *
 * @example
 *  var myFunction = function(result) { console.log(result); };
 *  tomtom.searchKey(<Your API key>); // this is optional if you passed global key earlier
 *  var geometryList = [{"type":"POLYGON","vertices":
 *                          ["37.7524152343544,-122.43576049804686",
 *                           "37.70660472542312,-122.43301391601562",
 *                           "37.712059855877314,-122.36434936523438",
 *                           "37.75350561243041,-122.37396240234374"]},
 *                      {"type": "CIRCLE", "position": "37.71205,-121.36434", "radius": 6000},{
 *                       "type": "CIRCLE", "position": "37.31205,-121.36434", "radius": 1000}];
 *  var geometrySearch = tomtom.geometrySearch().geometryList(geometryList).callback(myFunction).go();
 *
 * The SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @module Services
 * @class geometrySearch
 */
define('services/search/geometrySearch',['./search', '../validators', '../converters'], function(search, validators, converters) {
    'use strict';

    var searchPrototype = search.prototype.constructor;

    function GeometrySearch(options) {
        this.options = options;
        this.fields = {};

        this.fields.type = {
            defaultValue: this.TYPE.geometry,
            visible: false
        };
        /**
         * A setter and getter method for _'key'_ attribute for given search service instance.
         * Key can be set globally when initializing map with tomtom.key - for all services -
         * or tomtom.searchKey function - for all search service instances.
         *
         * Passing valid key in any way is required to enable geometry search service.
         *
         * The API Key can be issued from http://developer.tomtom.com.
         *
         * @method key
         * @chainable
         * @param {String} [key] key API Key valid for requested service.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {geometrySearch|String} Method returns the service instance allowing chaining or key value
         * if argument is omitted.
         */
        this.fields.key = {
            validator: validators.string
        };
        /**
         * A setter and getter for _'query'_ attribute. Value passed within this method represents text that will be
         * searched.
         *
         * This method is required to call, unless search value was passed by options.
         *
         * @method query
         * @chainable
         * @param {String} [query] Query string. Must be properly URL encoded.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {geometrySearch|String} Method returns the service instance allowing chaining or _query_ value
         * if argument is omitted.
         */
        this.fields.query = {
            validator: validators.string,
            required: true
        };
        /**
         * A setter and getter for _'limit'_ attribute. Represents maximum number of responses that will be
         * returned.
         *
         * Maximum value is 100.
         *
         * @method limit
         * @chainable
         * @param {integer} [limit=10] Maximum number of responses that will be returned.
         * @throws {TypeError} If the given argument cannot be converted to a valid value
         * @return {geometrySearch|integer} Method returns the service instance allowing chaining or _limit_ value
         * if argument is omitted.
         */
        this.fields.limit = {
            validator: validators.limit
        };

        /**
         * A setter and getter for _'language'_ attribute.
         * Whitelist is defined on [supported language codes list](https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
         *
         * @method language
         * @chainable
         * @param {String} [language] Language code, for example 'en-GB', 'en-US', 'de-DE'.
         *        Full list available on (https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
         * @throws {TypeError} If the given argument is unsupported.
         * @return {geometrySearch|String} Method returns the service instance allowing chaining or _language_ value if argument is omitted.
         */
        this.fields.language = {
            validator: validators.languageCode
        };

        /**
         * A setter and getter for _'geometryList'_ attribute. Represents list of geometries for the geometry
         * search.
         *
         * Available geometries are:
         * * polygons _(represent type "POLYGON", require list of vertices - points)_,
         * * circles _(represent type "CIRCLE", require position (point) and radius information)_.
         *
         * @method geometryList
         * @chainable
         * @param {Array} [idxSet] List of indexes, divided by commas.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {service|idxSet} Method returns the service instance allowing chaining or _geometryList_ value
         * if argument is omitted.
         */
        this.fields.geometryList = {
            validator: validators.geometryList,
            converter: converters.geometryList
        };

        /**
         * A setter and getter for _'idxSet'_ attribute. Represents list of parameters that allows to tune fuzzy
         * search service. List should be divided by commas.
         *
         * There are six predefined indexes, but there is a possibility to specify custom ones (if you have had
         * indexed your own content earlier).
         * The predefined indexes are:
         * * _POI_ Points of interest,
         * * _PAD_ Point Addresses,
         * * _Str_ Streets,
         * * _Xstr_ Cross Streets (intersections),
         * * _Geo_ Geographies,
         * * _Addr_ Address range interpolation (when there is no PAD).
         *
         * @method idxSet
         * @chainable
         * @param {String} [idxSet] List of indexes, divided by commas.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {service|idxSet} Method returns the service instance allowing chaining or _idxSet_ value
         * if argument is omitted.
         */
        this.fields.idxSet = {
            validator: validators.string
        };
        /**
         * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
         *
         * @method protocol
         * @chainable
         * @param {String} [protocol] Protocol type, one of _http_ or _https_.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {search|String} Method returns the service instance allowing chaining or _protocol_
         * value if argument is omitted.
         */
        this.fields.protocol = {validator: validators.protocol}
    }

    GeometrySearch.prototype = searchPrototype.prototype;
    GeometrySearch.prototype.constructor = GeometrySearch;

    /**
     * The function performing actual call to the web service. This method can be executed multiple times,
     * it is not changing parameters itself.
     *
     * Callback function takes a response parameter and fail callback takes error as only parameter.
     *
     * @method go
     * @param {Function} [callback] The callback function to be called on success.
     * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
     * fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
     * @return {Promise} If callback is omitted function returns promise
     */
    /**
     * A setter and getter for _callback_ function. This function is called after receiving successful response
     * from the service. It takes one argument which is the response - JSON described in go() function
     * documentation.
     *
     * @method callback
     * @chainable
     * @param {Function} [callback] The callback function to be called on success.
     * @throws {TypeError} if the given argument is not a function
     * @return {search} Method returns the service instance allowing chaining or function if argument is omitted.
     */
    /**
     * A setter and getter for the fail callback function. This function is called after an unsuccessful response
     * from the service or after a communication error. It takes one argument which is the error description.
     *
     * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
     * handle the response. When using promises, errors are handled with the second attribute of the then() method
     * or with the catch() method.
     *
     * By default the fail function is:
     * ```javascript
     *
     * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
     * ```
     * @method fail
     * @chainable
     * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
     *     the console and thrown.
     * @throws {TypeError} If the given argument is not a function.
     * @return {search|Function} Returns the service instance that allows chaining or a function, if the argument
     *     is omitted.
     */
    /**
     * Creates new instance of geometry search service.
     *
     * @method geometrySearch
     *
     * @param {Object}  [options] Options to be passed to the structured search call.
     * @param {String}  [options.key] The API Key issued from <a
     *     href="http://developer.tomtom.com">http://developer.tomtom.com</a>.
     * @param {String}  [options.query] Query string. Must be properly URL encoded.
     * @param {Array}   [options.geometryList] Array of geometries for the geometry search, accepts _polygons_ and
     *     _circles_.
     * @param {integer} [options.limit=10] Maximum number of responses that will be returned.
     * @param {String}  [options.language] Language code, for example 'en-GB', 'en-US', 'de-DE'.
     *        Full list available on (https://developer.tomtom.com/online-search/online-search-documentation/supported-languages)
     * @param {String}  [options.idxSet] List of indexes that tunes geometry search service.
     * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
     * @param {Function} [options.fail] The callback function to be called on failure.
     * @param {Function} [options.callback] Specifies the callback method. Only used where format is jsonp.
     *
     * @example
     * ```javascript
     *
     *  var myFunction = function(result) { console.log(result); };
     *  tomtom.searchKey(<Your API key>); // this is optional if you passed global key earlier
     *  var geometryList = [{"type":"POLYGON","vertices":
         *                          ["37.7524152343544,-122.43576049804686",
         *                           "37.70660472542312,-122.43301391601562",
         *                           "37.712059855877314,-122.36434936523438",
         *                           "37.75350561243041,-122.37396240234374"]},
     *                      {"type": "CIRCLE", "position": "37.71205,-121.36434", "radius": 6000},{
         *                       "type": "CIRCLE", "position": "37.31205,-121.36434", "radius": 1000}];
     *  var geometrySearch = tomtom.geometrySearch().geometryList(geometryList).callback(myFunction).go();
     *
     *  ```
     */
    return function(options) {
        return (new GeometrySearch(options)).construct();
    };
});

